import type { Event, EventHint } from '@sentry/react';

const FETCH_FAILURE_WITH_HOST =
    /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \(([^)]+)\)$/i;
const FETCH_FAILURE_WITHOUT_HOST = /^(?:Failed to fetch|Load failed)$/i;
const SEGMENT_PLUGIN_FACTORY_FAILURE = 'Failed to create PluginFactory [object Object]';
const CEF_SHARP_UPDATE_REJECTION =
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4$/;
const GRAPHQL_ABORT_REJECTION = 'AbortError: The operation was aborted.';

// Browser privacy tools commonly block these optional observability and analytics requests.
// Keep this list narrow so application network failures still reach Sentry.
const NOISY_FETCH_HOST_PATTERNS = [
    /^cdn\.segment\.com$/i,
    /^api\.segment\.io$/i,
    /^browser-intake-datadoghq\./i,
];

function isConsoleOriginEvent(event: Event) {
    if (event.logger === 'console') {
        return true;
    }

    const exceptionValues = event.exception?.values ?? [];
    if (
        exceptionValues.some(
            (value) =>
                value.mechanism?.type === 'console' ||
                value.mechanism?.type === 'auto.core.capture_console',
        )
    ) {
        return true;
    }

    const lastBreadcrumb = event.breadcrumbs?.at(-1);
    return (
        exceptionValues.length === 0 &&
        !!event.message &&
        lastBreadcrumb?.category === 'console' &&
        lastBreadcrumb.level === 'error' &&
        lastBreadcrumb.message === event.message &&
        lastBreadcrumb.data?.logger === 'console'
    );
}

function getEventMessage(event: Event, hint?: EventHint) {
    return (
        event.message ||
        event.logentry?.message ||
        event.exception?.values?.[0]?.value ||
        hint?.originalException?.toString?.() ||
        ''
    );
}

function isNoisyFetchHost(host: string) {
    return NOISY_FETCH_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

function getHost(url: unknown) {
    if (typeof url !== 'string') {
        return null;
    }

    try {
        return new URL(url).hostname;
    } catch {
        return null;
    }
}

function isSegmentActionPluginUrl(url: unknown) {
    if (typeof url !== 'string') {
        return false;
    }

    try {
        const parsedUrl = new URL(url);
        return (
            parsedUrl.hostname === 'cdn.segment.com' &&
            parsedUrl.pathname.startsWith('/next-integrations/actions/')
        );
    } catch {
        return false;
    }
}

function isSegmentPluginFactoryFailure(event: Event, message: string) {
    if (message !== SEGMENT_PLUGIN_FACTORY_FAILURE) {
        return false;
    }

    const breadcrumbs = event.breadcrumbs ?? [];
    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    if (
        lastBreadcrumb?.category !== 'console' ||
        lastBreadcrumb.level !== 'error' ||
        lastBreadcrumb.message !== SEGMENT_PLUGIN_FACTORY_FAILURE
    ) {
        return false;
    }

    const args = lastBreadcrumb.data?.arguments;
    if (!Array.isArray(args)) {
        return false;
    }

    const plugin = args[1];
    return (
        typeof plugin === 'object' &&
        plugin !== null &&
        isSegmentActionPluginUrl((plugin as Record<string, unknown>).url)
    );
}

function isSegmentQueueQuotaError(event: Event) {
    const nearestFailedFetch = event.breadcrumbs
        ?.slice(-3)
        .reverse()
        .find((breadcrumb) => breadcrumb.category === 'fetch' && breadcrumb.level === 'error');
    const recentSegmentFailure = getHost(nearestFailedFetch?.data?.url) === 'api.segment.io';

    return event.exception?.values?.some(
        (value) =>
            value.type === 'QuotaExceededError' &&
            (((value.value ?? '').includes('persisted-queue:v1:') &&
                (value.value ?? '').includes(':dest-Segment.io:')) ||
                (recentSegmentFailure &&
                    value.stacktrace?.frames?.some((frame) =>
                        frame.filename?.includes('/assets/metric-helpers.'),
                    ) &&
                    value.stacktrace?.frames?.some((frame) => frame.function === 'setItem'))),
    );
}

function isCefSharpUpdateRejection(event: Event) {
    const exceptionValues = event.exception?.values ?? [];
    return exceptionValues.some(
        (value) =>
            value.type === 'UnhandledRejection' &&
            value.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection' &&
            value.stacktrace == null &&
            CEF_SHARP_UPDATE_REJECTION.test(value.value ?? ''),
    );
}

function isExpectedGraphqlAbortRejection(event: Event, apiHost?: string) {
    if (!apiHost || event.exception?.values?.length !== 1) {
        return false;
    }

    const isStacklessBrowserRejection = event.exception?.values?.some(
        (value) =>
            value.type === 'Error' &&
            value.value === GRAPHQL_ABORT_REJECTION &&
            value.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection' &&
            value.stacktrace == null,
    );
    if (!isStacklessBrowserRejection) {
        return false;
    }

    const isGraphqlFetch = (breadcrumb: NonNullable<Event['breadcrumbs']>[number]) => {
        try {
            const url = new URL(String(breadcrumb.data?.url));
            return url.origin === new URL(apiHost).origin && url.pathname === '/api/graphql/';
        } catch {
            return false;
        }
    };

    // Only the nearest recent failed fetch can identify the rejected request.
    const recentBreadcrumbs = (event.breadcrumbs ?? []).slice(-3);
    for (let index = recentBreadcrumbs.length - 1; index >= 0; index -= 1) {
        const breadcrumb = recentBreadcrumbs[index];
        if (breadcrumb.category !== 'fetch' || breadcrumb.level !== 'error') {
            continue;
        }

        return isGraphqlFetch(breadcrumb);
    }

    // A canceled Apollo query may leave no failed-fetch breadcrumb: the new route's
    // successful requests can occupy the breadcrumb buffer before Sentry captures
    // the rejection. Require a recent navigation, an app GraphQL operation on the
    // new route, and a first-party GraphQL fetch rather than dropping all aborts.
    const routeBreadcrumbs = (event.breadcrumbs ?? []).slice(-20);
    const navigationIndex = routeBreadcrumbs.findLastIndex(
        (breadcrumb) => breadcrumb.category === 'navigation',
    );
    return (
        navigationIndex >= 0 &&
        routeBreadcrumbs
            .slice(navigationIndex + 1)
            .some((breadcrumb) => breadcrumb.category === 'gql') &&
        routeBreadcrumbs.some(
            (breadcrumb) => breadcrumb.category === 'fetch' && isGraphqlFetch(breadcrumb),
        )
    );
}

function isApolloQueryTeardownAbort(event: Event) {
    if (event.exception?.values?.length !== 1) {
        return false;
    }

    const value = event.exception.values[0];
    if (
        value.type !== 'AbortError' ||
        value.value !== 'The operation was aborted.' ||
        value.mechanism?.type !== 'auto.browser.global_handlers.onunhandledrejection'
    ) {
        return false;
    }

    // Apollo aborts active requests while useQuery tears down a subscription.
    // Match the browser's raw stack functions, which are available before source maps run.
    const frames = value.stacktrace?.frames ?? [];
    return (
        frames.some((frame) => frame.function?.includes('tearDownQuery')) &&
        frames.some((frame) => frame.function?.includes('abortActiveOperations'))
    );
}

function nearestFailedFetchIsNoisy(event: Event) {
    const breadcrumbs = event.breadcrumbs ?? [];
    for (let index = breadcrumbs.length - 1; index >= 0; index -= 1) {
        const breadcrumb = breadcrumbs[index];
        if (breadcrumb.category !== 'fetch' || breadcrumb.level !== 'error') {
            continue;
        }

        const host = getHost(breadcrumb.data?.url);
        return host !== null && isNoisyFetchHost(host);
    }

    return false;
}

export function shouldDropSentryEvent(event: Event, hint?: EventHint, apiHost?: string) {
    if (
        isCefSharpUpdateRejection(event) ||
        isExpectedGraphqlAbortRejection(event, apiHost) ||
        isApolloQueryTeardownAbort(event)
    ) {
        return true;
    }

    if (!isConsoleOriginEvent(event)) {
        return false;
    }

    if (isSegmentQueueQuotaError(event)) {
        return true;
    }

    const message = getEventMessage(event, hint);
    if (isSegmentPluginFactoryFailure(event, message)) {
        return true;
    }

    const hostMatch = FETCH_FAILURE_WITH_HOST.exec(message);
    if (hostMatch) {
        return isNoisyFetchHost(hostMatch[1]?.trim() ?? '');
    }

    return FETCH_FAILURE_WITHOUT_HOST.test(message) && nearestFailedFetchIsNoisy(event);
}
